import { Distinct, GetFormattedDateTime } from "../../../Utils/helpers";
import ApiClient from "../api-client";
import { cacheService, EnvType } from "../services";
import { AclWorkflowAccessLevel } from "../Types/acl-workflow-access-level";
import { CreateInstanceRequest } from "../Types/create-instance-request";
import { CreateInstanceResponse } from "../Types/create-instance-response";
import { CreateInstancesResponse } from "../Types/create-instances-response";
import { AdminAssetSinglePendingUploadDetails, DbAssetObjectInfos } from "../Types/db-asset-object-infos";
import { DbWorkflowDefinitionInfos } from "../Types/db-workflow-definition-infos";
import { DbWorkflowInstanceInfos } from "../Types/db-workflow-instance-infos";
import { DbWorkflowUserTasksInfos } from "../Types/db-workflow-user-tasks-infos";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetWorkflowDefinitionsResponse } from "../Types/get-workflow-definitions-response";
import { GetWorkflowInstanceDetailsResponse } from "../Types/get-workflow-instance-details-response";
import { GetWorkflowInstanceEnumsResponse } from "../Types/get-workflow-instance-enums-response";
import { GetWorkflowInstanceResponse } from "../Types/get-workflow-instance-response";
import { ListWorkflowInstanceResponse } from "../Types/list-workflow-instance-response";
import { ObjectStatus } from "../Types/object-status";
import { SetWorkflowInstanceFollowersRequest } from "../Types/set-workflow-instance-followers-request";
import { WorkflowStatus } from "../Types/workflow-status";
import { WorkflowType } from "../Types/workflow-type";
import WorkflowApiClient from "./workflow-api-client";
import { DbUserInfos } from "../Types/db-user-infos";
import { WorkflowFamily } from "../Types/workflow-family";
import { ObjectClass } from "../Types/object-class";
import { ObjectType } from "../Types/object-type";
import { IngestStatus } from "../../../Pages/admin/Ingest/IngestIndex/IngestProcessing/ingest-processing";
import { WorkflowActivity } from "../Types/workflow-activity";
import { WorkflowActivityLog } from "../Types/workflow-activity-log";
import GlobalConst from "../../Utils/Constants/globals";
import { DbKalikoWorkflowModelInfos, GetKalikoBPMWorkflowExecResponse } from "../Types/kaliko";
import { ListNodeResponse } from "../Types/list-node-response";
import { WorkflowTag } from "../Types/workflow-tag";

export interface WorkflowRows {
  id: number;
  createdByUserName: string;
  assetId: number;
  assetParentTitleId: number | null;
  assetParentObjectId: number | null;
  assetParentObjectName: string;
  assetParentTitleName: string;
  assetName: string;
  assetType: ObjectType;
  assetClass: ObjectClass | null;
  endTime: string | null;
  createdAt: string;
  providerName: string;
  status: IngestStatus;
  progress: number;
  priority: number;
  dueDate: Date | null;
  followersIds: number[];
  currentTaskName: string | null;
  annotation: string | null;
  userTasks: DbWorkflowUserTasksInfos[] | null;
  asset: DbAssetObjectInfos | null;
  currentActivities: WorkflowActivity[] | null;
  startTime: Date | null;
  logs: WorkflowActivityLog[] | null;
  type: WorkflowType | null;
  family: WorkflowFamily | null;
  platformId: number;
}

export interface WorkflowProps {
  rows: WorkflowRows[];
  countInstances: {
    countInstancesAccepted: number;
    countInstancesProcessing: number;
    countInstancesPendingQC: number;
    countInstancesRejected: number;
    countInstancesError: number;
    countInstancesWaitingMezz: number;
    countInstancesQcInProgress: number;
    countInstancesMissingMetadata: number;
  };
  filtersData: {
    createdByNames: string[];
    providersNames: string[];
  };
}

export function GetCountOfInstanceThatMatchStatuses(instances: WorkflowRows[], statuses: IngestStatus[]): number {
  if (instances === null || instances.length === 0) {
    return 0;
  }
  // TODO : use this everywhere when counting instead of filter to optimize : filter creates another array, this don't
  const count = instances.reduce((count, item) => (statuses.includes(item.status) ? ++count : count), 0);
  return count;
}

export function MapInstanceInfosToWorkflowRows(instancesInfos: DbWorkflowInstanceInfos[]): WorkflowRows[] {
  if (instancesInfos === null || instancesInfos.length === 0) return [];

  let mappedData: WorkflowRows[] = [];

  instancesInfos.forEach((instance) => {
    mappedData.push({
      createdByUserName: instance.createdByUserName,
      id: instance.id,
      assetParentTitleId: instance.asset.parentTitleId,
      assetParentObjectId: instance.asset.parentObjectId,
      assetName: instance.asset.name || "",
      assetParentTitleName: instance.asset.parentTitleName || "Drive",
      assetParentObjectName: instance.asset.comment || "",
      assetType: instance.asset.type,
      assetClass: instance.asset.classification,
      createdAt: instance.creationInfos.date.toString(),
      endTime: instance.endTime?.toString() || null,
      providerName: instance.labProviderName,
      status: GetIngestStatusFromInstance(instance),
      progress: instance.progress || 0,
      priority: instance.priority,
      dueDate: instance.triggerAssetDueDate,
      assetId: instance.asset.id,
      followersIds: instance.asset.followersInfos?.flatMap((x) => x.userId!) || [],
      annotation: instance.annotation,
      asset: instance.asset,
      currentTaskName: instance.currentTaskName,
      userTasks: instance.userTasks,
      currentActivities: instance.currentActivities,
      startTime: instance.startTime,
      logs: instance.logs,
      type: instance.definitionType,
      family: instance.definitionFamily,
      platformId: instance.platformId || 0,
    });
  });

  return mappedData;
}

export function GetIngestStatusFromInstance(instanceInfos: DbWorkflowInstanceInfos): IngestStatus {
  const workflowStatus = instanceInfos.status;
  const assetStatus = instanceInfos.asset.status;

  switch (workflowStatus) {
    case WorkflowStatus.READY:
      return IngestStatus.Ready;

    case WorkflowStatus.CREATED:
      return IngestStatus.Created;

    case WorkflowStatus.RUNNING:
      switch (assetStatus) {
        case ObjectStatus.ToBeQCed:
          return IngestStatus.PendingQC;
        case ObjectStatus.WaitingForMEZZReference:
          return IngestStatus.WaitingMezz;
        case ObjectStatus.QualityCheckInProgress:
          return IngestStatus.QcInProgress;
        case ObjectStatus.MissingMetadata:
          return IngestStatus.MissingMetadata;
        default:
          return IngestStatus.Running;
      }

    case WorkflowStatus.FAILED:
      return assetStatus === ObjectStatus.RejectedAuto ? IngestStatus.RejectedAuto : IngestStatus.WorkflowError;

    case WorkflowStatus.COMPLETED:
      switch (assetStatus) {
        case ObjectStatus.Accepted:
        case ObjectStatus.Broadcasted:
          return IngestStatus.Accepted;
        case ObjectStatus.Rejected:
          return IngestStatus.Rejected;
        case ObjectStatus.NeedCheckOrEditing:
          return IngestStatus.NeedCheckOrEdit;
        case ObjectStatus.QualityCheckInProgress:
          return IngestStatus.QcInProgress;
        case ObjectStatus.Deleted:
          return IngestStatus.Deleted;
        case ObjectStatus.RejectedAuto:
          return IngestStatus.RejectedAuto;
        default:
          return IngestStatus.Unknown;
      }

    case WorkflowStatus.ABORTED:
      return assetStatus === ObjectStatus.RejectedAuto ? IngestStatus.Rejected : IngestStatus.Aborted;

    case WorkflowStatus.CANCELLED:
      return IngestStatus.Cancelled;

    default:
      return IngestStatus.Unknown;
  }
}

export default class WorkflowService {
  workflowApiClient: WorkflowApiClient;

  constructor(envType: EnvType) {
    this.workflowApiClient = new WorkflowApiClient(new ApiClient(envType), envType);
  }

  async GetWorkflowsByFamily(
    workflowFamily: WorkflowFamily,
    days: number = 30,
    tag: WorkflowTag | undefined = undefined
  ): Promise<WorkflowProps> {
    const instances = (await this.workflowApiClient.GetInstancesByFamilyAndDays(workflowFamily, days, tag)).instances;

    const computedInstances = MapInstanceInfosToWorkflowRows(instances);

    const data: WorkflowProps = {
      rows: computedInstances,
      countInstances: {
        countInstancesAccepted: GetCountOfInstanceThatMatchStatuses(computedInstances, [IngestStatus.Accepted]),
        countInstancesQcInProgress: GetCountOfInstanceThatMatchStatuses(computedInstances, [IngestStatus.QcInProgress]),
        countInstancesProcessing: GetCountOfInstanceThatMatchStatuses(computedInstances, [
          IngestStatus.Running,
          IngestStatus.QcInProgress,
        ]),
        countInstancesPendingQC: GetCountOfInstanceThatMatchStatuses(computedInstances, [IngestStatus.PendingQC]),
        countInstancesRejected: GetCountOfInstanceThatMatchStatuses(computedInstances, [
          IngestStatus.Rejected,
          IngestStatus.RejectedAuto,
        ]),
        countInstancesError: GetCountOfInstanceThatMatchStatuses(computedInstances, [
          IngestStatus.WorkflowError,
          IngestStatus.Aborted,
          IngestStatus.Cancelled,
        ]),
        countInstancesWaitingMezz: GetCountOfInstanceThatMatchStatuses(computedInstances, [IngestStatus.WaitingMezz]),
        countInstancesMissingMetadata: GetCountOfInstanceThatMatchStatuses(computedInstances, [
          IngestStatus.MissingMetadata,
        ]),
      },
      filtersData: {
        createdByNames: computedInstances
          .map((x) => x.createdByUserName)
          .filter(Distinct)
          .sort(),
        providersNames: computedInstances
          .map((x) => x.providerName)
          .filter(Distinct)
          .sort(),
      },
    };

    return data;
  }

  public GetWorkflowsComputed(request: DbWorkflowInstanceInfos): WorkflowRows {
    const computedInstance = MapInstanceInfosToWorkflowRows([request]);
    return computedInstance[0];
  }

  async HandleCreateInstance(request: CreateInstanceRequest): Promise<string[]> {
    const response = await this.workflowApiClient.CreateInstance(request);
    if (!response.success && response.message !== null) {
      if (response.rescode === -4)
        // There already exists a request to purge this asset.
        return [response.message, "warning"];
      else return [response.message, "error"];
    }
    if (response.rescode === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID) {
      return [response.message!, "primary"];
    } else return [`Asset #${request.assetId} purge request done successfully!`, "success"];
  }

  async AssetByPassRejection(assetId: number, comment?: string): Promise<CreateInstanceResponse> {
    return await this.workflowApiClient.CreateInstance({
      assetId,
      definitionType: WorkflowType.BypassRejection,
      annotation: comment || null,
      definitionId: 0,
    });
  }

  async CreateInstance(request: CreateInstanceRequest): Promise<CreateInstanceResponse> {
    return await this.workflowApiClient.CreateInstance(request);
  }
  async SetPriorityOnInstanceById(instanceId: number, priority: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.SetPriorityOnInstanceById(instanceId, priority);
  }

  async NotifyResync(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.NotifyResync(instanceId);
  }

  async GetUserTasksForAssetSingle(assetId: number): Promise<AdminAssetSinglePendingUploadDetails | undefined> {
    const response = await this.workflowApiClient.ListUserTaskByObjectId(assetId);
    const instances = response.instances;
    const runningInstance = instances?.find((x) => x.status === WorkflowStatus.RUNNING);
    if (!runningInstance || !runningInstance.userTasks?.length) return undefined;

    const userInfos: DbUserInfos = cacheService.GetUserInfo()!;

    return {
      createdBy: runningInstance.createdByUserName || "-",
      startDate: GetFormattedDateTime(runningInstance.creationInfos.date),
      currentTaskName: runningInstance.currentTaskName || "-",
      isClaimedByUser: runningInstance.userTasks[0].claimInfos !== null,
      userHasRightToClaim: true,
      userHasRightToComplete: true,
      task: runningInstance.userTasks[0],
      workflowInstanceId: runningInstance.id,
    };
  }

  //------DO NOT USE------

  async GetInstancesByFamily(
    definitionFamily: WorkflowFamily,
    pageNb: number,
    pageSize: number
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByFamily(definitionFamily, pageNb, pageSize);
  }

  async GetInstancesByFamilyAndDays(
    definitionFamily: WorkflowFamily,
    days: number,
    tag: WorkflowTag | undefined = undefined
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByFamilyAndDays(definitionFamily, days, tag);
  }

  async GetDefinitions(): Promise<GetWorkflowDefinitionsResponse> {
    return await this.workflowApiClient.GetDefinitions();
  }
  async GetDefinitionsByObject(objectId: number): Promise<GetWorkflowDefinitionsResponse> {
    return await this.workflowApiClient.GetDefinitionsByObject(objectId);
  }
  async CreateDefinition(request: DbWorkflowDefinitionInfos): Promise<GenericResponseStd> {
    return await this.workflowApiClient.CreateDefinition(request);
  }
  async UpdateDefinition(request: DbWorkflowDefinitionInfos): Promise<GenericResponseStd> {
    return await this.workflowApiClient.UpdateDefinition(request);
  }
  async DeleteDefinition(id: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.DeleteDefinition(id);
  }
  async GetInstancesByDefinitionType(
    definitionType: string,
    pageNb: number,
    pageSize: number
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByDefinitionType(definitionType, pageNb, pageSize);
  }
  async GetInstancesByDefinitionTypeAndAccessLevel(
    definitionType: string,
    pageNb: number,
    pageSize: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByDefinitionTypeAndAccessLevel(
      definitionType,
      pageNb,
      pageSize,
      workflowAccessLevel
    );
  }
  async GetInstancesByDefinitionTypeAndDate(
    definitionType: string,
    startDateAsString: string,
    endDateAsString: string
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByDefinitionTypeAndDate(
      definitionType,
      startDateAsString,
      endDateAsString
    );
  }
  async GetInstancesByDefinitionTypeDateAndAccessLevel(
    definitionType: string,
    startDateAsString: string,
    endDateAsString: string,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByDefinitionTypeDateAndAccessLevel(
      definitionType,
      startDateAsString,
      endDateAsString,
      workflowAccessLevel
    );
  }

  async GetInstancesByFamilyAndAccessLevel(
    definitionFamily: string,
    pageNb: number,
    pageSize: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByFamilyAndAccessLevel(
      definitionFamily,
      pageNb,
      pageSize,
      workflowAccessLevel
    );
  }
  async GetInstancesByFamilyDaysAndAccessLevel(
    definitionFamily: string,
    pageNb: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByFamilyDaysAndAccessLevel(
      definitionFamily,
      pageNb,
      workflowAccessLevel
    );
  }
  async GetInstancesByFamilyAndDate(
    definitionFamily: string,
    startDateAsString: string,
    endDateAsString: string
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByFamilyAndDate(
      definitionFamily,
      startDateAsString,
      endDateAsString
    );
  }
  async GetInstancesByFamilyDateAndAccessLevel(
    definitionFamily: string,
    startDateAsString: string,
    endDateAsString: string,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByFamilyDateAndAccessLevel(
      definitionFamily,
      startDateAsString,
      endDateAsString,
      workflowAccessLevel
    );
  }
  async DownloadInstancesByDefinitionFamilyAsCSV(
    definitionFamily: string,
    startDateAsString: string,
    endDateAsString: string
  ): Promise<GenericResponseStd> {
    return await this.workflowApiClient.DownloadInstancesByDefinitionFamilyAsCSV(
      definitionFamily,
      startDateAsString,
      endDateAsString
    );
  }
  async GetInstancesByAssetId(assetId: number): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetInstancesByAssetId(assetId);
  }
  async GetInstanceById(instanceId: number): Promise<GetWorkflowInstanceDetailsResponse> {
    return await this.workflowApiClient.GetInstanceById(instanceId);
  }

  async CreateInstances(requests: CreateInstanceRequest[]): Promise<CreateInstancesResponse> {
    return await this.workflowApiClient.CreateInstances(requests);
  }
  async PlayInstance(instanceId: number, inboundVars?: Record<string, string>): Promise<GenericResponseStd> {
    return await this.workflowApiClient.PlayInstance(instanceId, inboundVars);
  }
  async RetryInstance(instanceId: number, fromBeggining: boolean = false): Promise<GenericResponseStd> {
    return await this.workflowApiClient.RetryInstance(instanceId, fromBeggining);
  }
  async RetryInstanceScratch(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.RetryInstanceScratch(instanceId);
  }
  async AbortInstance(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.AbortInstance(instanceId);
  }
  async DeleteInstance(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.DeleteInstance(instanceId);
  }
  async SetWorkflowInstanceFollowers(request: SetWorkflowInstanceFollowersRequest): Promise<GenericResponseStd> {
    return await this.workflowApiClient.SetWorkflowInstanceFollowers(request);
  }
  async GetUserTaskEnums(): Promise<GetWorkflowInstanceEnumsResponse> {
    return await this.workflowApiClient.GetUserTaskEnums();
  }
  async ListUserTasks(): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.ListUserTasks();
  }
  async ListUserTasksByPages(pageNb: number, pageSize: number): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.ListUserTasksByPages(pageNb, pageSize);
  }
  async GetUserTaskByInstanceId(instanceId: number): Promise<GetWorkflowInstanceResponse> {
    return await this.workflowApiClient.GetUserTaskByInstanceId(instanceId);
  }
  async ListUserTaskByObjectId(objectId: number): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.ListUserTaskByObjectId(objectId);
  }
  async ListUserTaskByStatus(status: WorkflowStatus): Promise<ListWorkflowInstanceResponse> {
    return await this.workflowApiClient.ListUserTaskByStatus(status);
  }
  async ClaimUserTask(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.ClaimUserTask(instanceId);
  }
  async UnclaimUserTask(instanceId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.UnclaimUserTask(instanceId);
  }
  async ClaimUserTaskByUserId(instanceId: number, userId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.ClaimUserTaskByUserId(instanceId, userId);
  }
  async UpdateUserTaskMetadata(userTask: DbWorkflowUserTasksInfos, workflowId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.UpdateUserTaskMetadata(userTask, workflowId);
  }
  async CompleteUserTaskMetadata(userTask: DbWorkflowUserTasksInfos, workflowId: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.CompleteUserTaskMetadata(userTask, workflowId);
  }
  async GetNodes(): Promise<ListNodeResponse> {
    return await this.workflowApiClient.GetNodes();
  }
  async GetWorkflowModels(): Promise<DbKalikoWorkflowModelInfos[]> {
    return await this.workflowApiClient.GetWorkflowModels();
  }
  async UpdateWorkflowModels(workflow: DbKalikoWorkflowModelInfos): Promise<GenericResponseStd> {
    return await this.workflowApiClient.UpdateWorkflowModels(workflow);
  }
  async CreateWorkflowModels(workflow: DbKalikoWorkflowModelInfos): Promise<GenericResponseStd> {
    return await this.workflowApiClient.CreateWorkflowModels(workflow);
  }
  async GetKalikoBPMWorkflowExecById(id: number): Promise<GetKalikoBPMWorkflowExecResponse> {
    return await this.workflowApiClient.GetKalikoBPMWorkflowExecById(id);
  }
  async GetKalikoBPMWorkflowExecByGuid(guid: string): Promise<GetKalikoBPMWorkflowExecResponse> {
    return await this.workflowApiClient.GetKalikoBPMWorkflowExecByGuid(guid);
  }
  async DeleteKalikoBPMWorkflowModel(id: number): Promise<GenericResponseStd> {
    return await this.workflowApiClient.DeleteKalikoBPMWorkflowModel(id);
  }
  //------Utils------
  GetServiceUrl(): string {
    return this.workflowApiClient.GetApiUrl();
  }
}
